import * as React from 'react'

import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import {
  collisionPadding,
  IS_SELECTED,
  SELECTED_TIER_PLACEMENT,
} from '~/client/src/shared/map/labelLayerStyle'
import { zoomScaledSize } from '~/client/src/shared/map/mapZoomRule'
import { MARKER_CLEARANCE_PX } from '~/client/src/shared/map/markerGeometry'

import {
  DELIVERY_PINS_LAYER,
  pinTwinLayerIds,
  WORKFLOW_PINS_LAYER,
} from '../MapBoxViewer/mapboxConstants'
import {
  ICON_OCCLUSION_PAINT,
  ICON_TEXT_OCCLUSION_PAINT,
} from '../MapBoxViewer/mapOcclusionRule'
import {
  queryFirstHit,
  useMapHoverProbe,
} from '../MapBoxViewer/useMapHoverProbe'
import { useReleaseWithoutFade } from '../MapBoxViewer/useReleaseWithoutFade'
import {
  COUNT_BUBBLE_CENTER_ABOVE_TIP_HOVERED_PX,
  COUNT_BUBBLE_CENTER_ABOVE_TIP_PX,
  getWorkflowPinImageKey,
  registerMissingWorkflowPinImage,
} from '../SitemapHelpers/utils/GLWorkflowPinImageGenerator'
import {
  buildWorkflowPinFeatures,
  IWorkflowPinPoint,
} from './workflowPinPoints'

export const WORKFLOW_PINS_LAYER_ID = WORKFLOW_PINS_LAYER
// Delivery mini-pins are the same art and the same anchored-lift model, so they
// render through this layer too — on their own ids, with their own points and
// click routing.
export const DELIVERY_PINS_LAYER_ID = DELIVERY_PINS_LAYER

// Shared with the host's onMapClick hit-test and hover-cursor probe — a pin can
// live on any of the four collision-variant layers.
export const workflowPinLayerIds = (
  layerId: string = WORKFLOW_PINS_LAYER_ID,
): string[] => [layerId, `${layerId}-counted`, ...pinTwinLayerIds(layerId)]

export const WORKFLOW_PIN_INTERACTIVE_LAYER_IDS = workflowPinLayerIds()

interface IProps {
  points: IWorkflowPinPoint[]
  // Defaults to the workflow pin ids; pass a distinct id to mount a second set.
  layerId?: string
  visible?: boolean
  isZoomScalingEnabled?: boolean
  // The pin's size at full zoom, its count and lift with it.
  iconScale?: number
  // The size of the markers anchored pins ride, at full zoom; the pin's own unless given. The lift
  // clears the marker, so it follows the marker's size.
  markerScale?: number
  // The pin layers aren't interactiveLayerIds, so the host drives the cursor from this signal.
  // The layer id comes back with it: several pin sets share one map.
  onHoverChange?: (hovered: boolean, source: string) => void
  // False detaches the hover probe entirely — pins go inert (no pointer, no
  // hit-test) so an active drawing gesture stays clean and continuous.
  interactive?: boolean
  // Opt-in click routing for hosts with no map-click plumbing of their own.
  // LogisticsMapView instead hit-tests inside its own onMapClick, where pin taps
  // must be resolved in a set order against markups and whiteboard-deselect.
  onPinClick?: (coordKey: string) => void
  // Reports which pin group is under the pointer, so sibling layers naming the same group
  // (the pin label) can highlight with it. Separate from onHoverChange, which is the
  // cursor signal and is shared by every pin set on the map.
  onHoveredCoordKeyChange?: (coordKey: string) => void
  // Layers drawn beside the pins whose features carry the same coordKey — the shared caption
  // chip. A tap on the label opens what the pin opens, the parity a location's boxed label
  // already has. Hit-tested after the pins: a selected caption draws above them.
  extraInteractiveLayerIds?: string[]
  // Every pin draws, whatever it overlaps: a host that places its own labels needs them all.
  isOverlapForced?: boolean
}

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

const COUNT_TEXT_SIZE = 14
// The tip clears the anchor marker, so a pin that declutters never culls the marker it rides.
// Shared with the host's popup offset, which must clear it.
export const ANCHORED_LIFT_PX = MARKER_CLEARANCE_PX

// Static, so it stays one object for the life of the module: react-map-gl's Layer updater
// short-circuits on reference equality, and a fresh object re-walks every paint expression
// on each of the two layers that carry it — once per pointer sample during a hover.
const COUNT_TEXT_PAINT = {
  'text-color': ['get', 'countColor'] as never,
  ...ICON_TEXT_OCCLUSION_PAINT,
}

const SINGLE_FILTER = ['==', ['get', 'count'], 1]
const COUNTED_FILTER = ['>', ['get', 'count'], 1]
// Only the selected pin is exempt from decluttering: it paints from twins in the selected tier,
// which file its box so the neighbours under it give way. Anchored pins declutter like any pin.
const EXEMPT_FILTER = IS_SELECTED
const NOT_EXEMPT_FILTER = ['!', EXEMPT_FILTER]

// GL symbol layers over four collision variants; pins ALWAYS declutter,
// deliberately ignoring the "Allow labels to overlap" setting (STX1-686).
const GLWorkflowPinLayer: React.FC<IProps> = ({
  points,
  layerId = WORKFLOW_PINS_LAYER_ID,
  visible = true,
  isZoomScalingEnabled = false,
  iconScale = 1,
  markerScale = iconScale,
  onHoverChange,
  onHoveredCoordKeyChange,
  interactive = true,
  onPinClick,
  extraInteractiveLayerIds,
  isOverlapForced = false,
}) => {
  const { current: map } = useMap()
  // Released, the pin is a new symbol on its declutter layer, and a placed symbol fades in.
  useReleaseWithoutFade(map, points.find(p => p.isSelected)?.coordKey ?? '')
  const hasPins = visible && points.length > 0
  const extraLayerKey = (extraInteractiveLayerIds ?? []).join('|')
  const pinLayerIds = React.useMemo(
    () => workflowPinLayerIds(layerId),
    [layerId],
  )
  const captionLayerIds = React.useMemo(
    () => extraInteractiveLayerIds ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraLayerKey],
  )

  // Pre-warm the pin images as soon as the data is known: registration is async (SVG decode),
  // so waiting for GL's styleimagemissing logs a "could not be loaded" warning per tile request
  // until the raster lands. The registrar is idempotent, and styleimagemissing stays as the
  // style-reload fallback.
  React.useEffect(() => {
    const gl = map?.getMap()
    if (!gl || !points.length) return
    const keys = new Set(
      points.map(p =>
        getWorkflowPinImageKey({
          iconName: p.iconName,
          withCount: p.count > 1,
          isSelected: false,
          isDone: p.isDone,
          isAssigned: p.isAssigned,
          isCanceled: !!p.isCanceled,
          isHovered: false,
          // All part of the key: pre-warm a different fill from the one the features ask
          // for and every tile requests a missing image.
          isShared: p.isShared,
          status: p.status,
          isLate: !!p.isLate,
          isMixed: !!p.isMixed,
          isDelivery: !!p.isDelivery,
        }),
      ),
    )
    // At the scale they are drawn, so an enlarged pin is rasterized sharp.
    keys.forEach(key => registerMissingWorkflowPinImage(gl, key, iconScale))
  }, [map, points, iconScale])
  // A dedicated 42px hover image — icon-size can't be feature-state driven.
  const [hoveredCoordKey, setHoveredCoordKey] = React.useState<string | null>(
    null,
  )
  const onHoverChangeRef = React.useRef(onHoverChange)
  onHoverChangeRef.current = onHoverChange
  const onHoveredCoordKeyChangeRef = React.useRef(onHoveredCoordKeyChange)
  onHoveredCoordKeyChangeRef.current = onHoveredCoordKeyChange

  useMapHoverProbe(map, {
    enabled: hasPins && interactive,
    layerIds: pinLayerIds,
    fallbackLayerIds: captionLayerIds,
    throttleMs: 80,
    onHit: hit => {
      const coordKey = (hit?.properties?.coordKey as string) ?? null
      setHoveredCoordKey(coordKey)
      onHoveredCoordKeyChangeRef.current?.(coordKey)
      onHoverChangeRef.current?.(!!hit, layerId)
    },
  })

  const onPinClickRef = React.useRef(onPinClick)
  onPinClickRef.current = onPinClick

  React.useEffect(() => {
    if (!map || !hasPins || !interactive || !onPinClick) return undefined
    const onClick = (e: mapboxgl.MapMouseEvent): void => {
      const coordKey = queryFirstHit(map, e.point, [
        pinLayerIds,
        captionLayerIds,
      ])?.properties?.coordKey as string
      if (coordKey) onPinClickRef.current?.(coordKey)
    }
    map.on('click', onClick)
    return () => {
      map.off('click', onClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, hasPins, interactive, !!onPinClick, pinLayerIds, captionLayerIds])

  const data = React.useMemo<GeoJSON.FeatureCollection>(() => {
    if (!visible || !points.length) {
      return EMPTY_FC
    }
    return {
      type: 'FeatureCollection',
      features: buildWorkflowPinFeatures(points, hoveredCoordKey),
    }
  }, [points, visible, hoveredCoordKey])

  // In the pin's own units: the lift clears the marker, so it takes the marker's size.
  const anchoredLiftPx = ANCHORED_LIFT_PX * (markerScale / iconScale)

  const iconLayout: Record<string, unknown> = {
    'icon-image': ['get', 'image'],
    // Images are rasterized with the pin tip at the exact bottom edge.
    'icon-anchor': 'bottom',
    'icon-size': zoomScaledSize(isZoomScalingEnabled, iconScale),
    // icon-offset is multiplied by icon-size, so the lift scales with the pin
    // (and the marker beneath, which shrinks by the same getZoomScale ramp).
    'icon-offset': [
      'case',
      ['get', 'anchored'],
      ['literal', [0, -anchoredLiftPx]],
      ['literal', [0, 0]],
    ],
    // On the ramp with every other collision inset. Held flat it was the one distance on the
    // map that did not shrink, so as the pin and the gap below it scaled down the inset ate
    // that gap and the pin evicted its own label — from about zoom 14 down.
    'icon-padding': collisionPadding(isZoomScalingEnabled),
    'symbol-sort-key': ['get', 'sortKey'],
  }

  const declutterLayout = {
    'icon-allow-overlap': isOverlapForced,
    'icon-ignore-placement': isOverlapForced,
  }

  // Anchored pins add the lift; text-offset ems scale with text-size exactly as
  // icon-offset px scale with icon-size, so the pair stays glued at every zoom.
  const countTextOffset = (bubbleCenterPx: number): unknown => [
    'case',
    ['get', 'anchored'],
    ['literal', [0, -((bubbleCenterPx + anchoredLiftPx) / COUNT_TEXT_SIZE)]],
    ['literal', [0, -(bubbleCenterPx / COUNT_TEXT_SIZE)]],
  ]

  // Count text rides the SAME symbol as the icon, so a culled pin never orphans
  // its number; always non-empty here, it sidesteps the empty-text-field
  // degenerate-collision-box quirk that forbids a shared icons+text layer.
  const countTextLayout: Record<string, unknown> = {
    'text-field': ['get', 'countLabel'],
    'text-font': ['Inter Medium', 'Arial Unicode MS Regular'],
    'text-size': zoomScaledSize(
      isZoomScalingEnabled,
      COUNT_TEXT_SIZE * iconScale,
    ),
    // The enlarged pin is taller, so its bubble center sits higher. Selected counts as
    // enlarged: it wears the same art whether or not the pointer is on it.
    'text-offset': [
      'case',
      ['any', ['get', 'hovered'], ['get', 'selected']],
      countTextOffset(COUNT_BUBBLE_CENTER_ABOVE_TIP_HOVERED_PX),
      countTextOffset(COUNT_BUBBLE_CENTER_ABOVE_TIP_PX),
    ],
    'text-allow-overlap': true,
    'text-ignore-placement': true,
  }

  // Mount order = style order (bottom→top); mapbox places TOPMOST layers first,
  // so counted outranks singles — an aggregate bubble carries more information.
  return (
    <Source id={`${layerId}-source`} type="geojson" data={data}>
      <Layer
        id={layerId}
        type="symbol"
        filter={['all', SINGLE_FILTER, NOT_EXEMPT_FILTER] as never}
        layout={{ ...iconLayout, ...declutterLayout } as never}
        paint={ICON_OCCLUSION_PAINT}
      />
      <Layer
        id={`${layerId}-counted`}
        type="symbol"
        filter={['all', COUNTED_FILTER, NOT_EXEMPT_FILTER] as never}
        layout={
          { ...iconLayout, ...declutterLayout, ...countTextLayout } as never
        }
        paint={COUNT_TEXT_PAINT}
      />
      {/* The selected pin's twins, raised into the selected tier by enforceShapeLayerOrder. The
          count text spreads last: it still ignores placement. */}
      <Layer
        id={`${layerId}-exempt`}
        type="symbol"
        filter={['all', SINGLE_FILTER, EXEMPT_FILTER] as never}
        layout={{ ...iconLayout, ...SELECTED_TIER_PLACEMENT } as never}
        paint={ICON_OCCLUSION_PAINT}
      />
      <Layer
        id={`${layerId}-exempt-counted`}
        type="symbol"
        filter={['all', COUNTED_FILTER, EXEMPT_FILTER] as never}
        layout={
          {
            ...iconLayout,
            ...SELECTED_TIER_PLACEMENT,
            ...countTextLayout,
          } as never
        }
        paint={COUNT_TEXT_PAINT}
      />
    </Source>
  )
}

// Memoized: the host re-renders every camera frame; `points` is a stable @computed.
export default React.memo(GLWorkflowPinLayer)
